import { Fragment, type MouseEvent, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from '@rocco/components/accordion';
import { CONTENT_CAP_PADDED } from './large-screen';

export interface NumberedAccordionRow {
    title: string;
    /** Heading text shown in the trigger, when it should differ from `title`.
     *  `title` stays the stable open-state key, so the visible heading can change
     *  (e.g. "Book a Meeting" vs "Get in touch") without breaking open/scroll
     *  state. Defaults to `title`. */
    displayTitle?: string;
    content?: ReactNode;
    /**
     * Makes the row a one-shot action rather than an expanding panel: the
     * heading becomes a button that fires this, nothing mounts beneath it, and
     * the chevron gives way to an arrow so the row stops promising a panel.
     */
    onSelect?: (event: MouseEvent<HTMLButtonElement>) => void;
    /** Extra classes on the row's `AccordionItem` (e.g. to let a row's content
     *  overflow so a sticky child works). */
    itemClassName?: string;
    /**
     * Extra classes on the row's `AccordionContent` — the element Radix clips
     * for the collapse animation. A row whose content must bleed past the page
     * gutter (a full-bleed carousel) widens the clip here with a negative margin
     * plus equal padding, which moves the clip boundary without moving the
     * content.
     */
    contentClassName?: string;
    /** DOM id on the row's `AccordionItem`, e.g. as a scroll-anchor target. */
    id?: string;
    /**
     * Keep the row's content in the DOM while closed (display: none) — Radix
     * unmounts closed rows, which strips the content from the crawled HTML.
     * Use for rows whose copy/links must stay indexable (e.g. the supplier
     * block on mobile, where the row is closed by default). Trades away the
     * close animation: the content hides the moment the row closes.
     */
    forceMount?: boolean;
    /**
     * Rendered straight after this row's item, outside the accordion item —
     * for a block that belongs between two rows rather than inside either
     * (e.g. the product page's centred action cluster).
     */
    after?: ReactNode;
}

export interface NumberedAccordionProps {
    rows: NumberedAccordionRow[];
    /** `light` = ink on white (product). `dark` = light type on a dark section. */
    theme?: 'light' | 'dark';
    /** Extra classes for the section wrapper (e.g. a page-specific gutter). */
    className?: string;
    /**
     * Override the inner content cap. The default assumes the section's 3rem
     * gutter (`CONTENT_CAP_PADDED`); callers overriding the gutter via
     * `className` pass the matching `120rem - 2 x gutter` cap so the capped
     * content still lands at container + gutter above 1920.
     */
    capClassName?: string;
    /** Extra classes for each row trigger (e.g. page-specific row metrics). */
    triggerClassName?: string;
    /** Allow several rows open at once (default: one at a time). */
    multiple?: boolean;
    /**
     * Drop the bottom hairline on the last row. Use when the accordion is the
     * terminal block on the page, so the closing line doesn't float as a stray
     * separator above the next section.
     */
    hideLastBorder?: boolean;
    /** Row titles open on load. Single mode uses the first entry only. */
    defaultOpenTitles?: string[];
    /** Controlled open-row(s): a title string (single mode) or an array of
     *  titles (multiple mode). When provided the accordion is controlled — pair
     *  with `onValueChange`. */
    value?: string | string[];
    /** Fires with the new open-row title(s) when a row toggles. */
    onValueChange?: (value: string | string[]) => void;
    /**
     * Heading level for the row triggers. Defaults to Rocco's `h3`; pass `2` on
     * a page where the accordion is a top-level section under the `h1`, so the
     * outline doesn't skip a level.
     */
    headingLevel?: 2 | 3 | 4;
}

/**
 * A numbered row that acts instead of expanding — same hairline, metrics and
 * type as a trigger, but a plain button carrying an arrow. Kept out of the
 * Radix item entirely: an AccordionItem whose trigger does not toggle still
 * announces itself as expandable.
 */
const ActionRow = ({
    id,
    className,
    triggerClassName,
    headingLevel = 3,
    onSelect,
    children,
}: {
    id?: string;
    className: string;
    triggerClassName: string;
    headingLevel?: 2 | 3 | 4;
    onSelect: (event: MouseEvent<HTMLButtonElement>) => void;
    children: ReactNode;
}) => {
    const Heading = `h${headingLevel}` as const;
    return (
        <div id={id} className={className}>
            <Heading className="rocco-accordion-header flex">
                <button
                    type="button"
                    onClick={onSelect}
                    className={cn('rocco-accordion-trigger', triggerClassName)}
                >
                    {children}
                    <ArrowRight aria-hidden="true" className="shrink-0" />
                </button>
            </Heading>
        </div>
    );
};

/**
 * Shared numbered accordion for the detail pages — hairline-separated rows with
 * an auto-incrementing "(0N)" prefix (regular weight) and the title, expanding
 * to the row content. The page builds the rows; self-hides when empty. Rows
 * are keyed by title, so `defaultOpenTitles` stays stable as conditional rows
 * shift the numbering.
 */
export const NumberedAccordion = ({
    rows,
    theme = 'light',
    className,
    capClassName,
    triggerClassName,
    multiple = false,
    hideLastBorder = false,
    defaultOpenTitles,
    value,
    onValueChange,
    headingLevel,
}: NumberedAccordionProps) => {
    if (rows.length === 0) return null;
    const isDark = theme === 'dark';

    const items = rows.map((row, index) => {
        const number = String(index + 1).padStart(2, '0');
        // Shared by both row kinds so an action row is typographically the
        // same row as an expanding one — only its affordance differs.
        const heading = (
            <span className="font-sans font-normal group-data-[state=open]:font-medium">
                <span aria-hidden className="font-normal">
                    ({number})
                </span>{' '}
                {row.displayTitle ?? row.title}
            </span>
        );
        const borderClass = cn(
            // `border-b-0` drops the rocco item's own bottom hairline —
            // left on, it stacks against the next row's `border-t` and
            // every separator between rows renders at 2px.
            'border-t border-b-0',
            hideLastBorder ? '' : 'last:border-b',
            isDark ? 'border-white/20' : 'border-black/20',
            row.itemClassName,
        );
        const triggerClass = cn(
            // Mobile row metrics are the shared platform default:
            // 26px trigger padding (~69px collapsed rows) and 16px
            // titles. Direct-child chevron size; a caller can override
            // per page via `triggerClassName` (e.g. About → 16px).
            'group flex w-full items-center justify-between py-[1.44rem] text-left max-md:py-[1.625rem] max-md:text-base md:text-[1.375rem] [&>svg]:size-5 md:[&>svg]:size-6',
            isDark ? 'text-white' : 'text-[#1f1f21]',
            triggerClassName,
        );

        const item = row.onSelect ? (
            <ActionRow
                key={`${row.title}-${index}`}
                id={row.id}
                className={borderClass}
                triggerClassName={triggerClass}
                headingLevel={headingLevel}
                onSelect={row.onSelect}
            >
                {heading}
            </ActionRow>
        ) : (
            <AccordionItem
                key={`${row.title}-${index}`}
                value={row.title}
                id={row.id}
                className={borderClass}
            >
                {/* Open row titles go medium, per the Figma. The (0N) prefix
                    shares the title colour (stays regular weight) and is
                    decorative — hidden from the trigger's accessible name. */}
                <AccordionTrigger
                    headingLevel={headingLevel}
                    className={triggerClass}
                >
                    {heading}
                </AccordionTrigger>
                <AccordionContent
                    forceMount={row.forceMount || undefined}
                    className={cn(
                        row.forceMount && 'data-[state=closed]:hidden',
                        row.contentClassName,
                    )}
                >
                    {row.content}
                </AccordionContent>
            </AccordionItem>
        );
        if (!row.after) return item;
        // A Fragment, not a wrapper element: the item stays a direct child of
        // the accordion root, so `last:border-b` still measures against the
        // real row siblings rather than seeing itself as the last of a group.
        return (
            <Fragment key={`${row.title}-${index}-group`}>
                {item}
                {row.after}
            </Fragment>
        );
    });

    const accordion = multiple ? (
        value !== undefined ? (
            <Accordion
                type="multiple"
                value={value as string[]}
                onValueChange={onValueChange as (value: string[]) => void}
                className="w-full"
            >
                {items}
            </Accordion>
        ) : (
            <Accordion
                type="multiple"
                defaultValue={defaultOpenTitles}
                className="w-full"
            >
                {items}
            </Accordion>
        )
    ) : value !== undefined ? (
        <Accordion
            type="single"
            collapsible
            value={value as string}
            onValueChange={onValueChange as (value: string) => void}
            className="w-full"
        >
            {items}
        </Accordion>
    ) : (
        <Accordion
            type="single"
            collapsible
            defaultValue={defaultOpenTitles?.[0]}
            className="w-full"
        >
            {items}
        </Accordion>
    );

    return (
        <section
            className={cn(
                // 20px shared mobile gutter (Product's metrics are the default).
                'max-md:px-5 qhd:overflow-x-clip md:px-[3rem]',
                isDark ? 'bg-transparent' : 'bg-ground',
                className,
            )}
        >
            <div className={capClassName ?? CONTENT_CAP_PADDED}>
                {accordion}
            </div>
        </section>
    );
};
